import { inject, Injectable } from '@angular/core';
import { map, Observable, tap } from 'rxjs';
import { AbstractService } from './abstract-service';
import { TranslationBundleService } from './translation-bundle.service';
import { PreferenceService } from './preference.service';
import { SolText } from '../models/core/sol-text';
import { SolValidationMessage } from '../models/core/sol-validation-message';
import { PreferenceTools } from '../tools/preference-tools';
import { SolSettingKey } from '../models/core/sol-setting-key';

export interface LocaleServiceState {
  locale: string;
  defaultLocale: string;
  locales: SolLocale[];
  bundleNames: string[];
  translations: Record<string, string>;
  translationsLocale: string;
  saveFailure?: SolValidationMessage;
}

export interface SolLocale {
  locale: string;
  languageId: string;
  name: string;
  localName: string;
}

export class SolLocales {
  public static readonly en: SolLocale = {
    locale: 'en-US',
    languageId: 'en',
    name: 'English',
    localName: 'sol-locale.language.en',
  };
}

@Injectable({
  providedIn: 'root',
})
export class LocaleService extends AbstractService<LocaleServiceState> {
  // Dependencies
  private translationBundleService = inject(TranslationBundleService);
  private preferenceService = inject(PreferenceService);

  constructor() {
    super({
      locale: SolLocales.en.locale,
      defaultLocale: SolLocales.en.locale,
      locales: [SolLocales.en],
      bundleNames: [],
      translations: {},
      translationsLocale: '',
    });
  }

  public initialize(bundleNames: string[], languages: SolLocale[] = [SolLocales.en]): Observable<void> {
    const cached = this.preferenceService.cached(SolSettingKey.locale);
    const fallback = this.state.locale;
    this.patch({
      bundleNames: bundleNames,
      locales: languages,
      defaultLocale: fallback,
      locale: LocaleService.toSupportedLocale(cached, languages) ?? fallback,
    });
    this.reconcile();
    return this.refreshBundles();
  }

  public setLocale(locale: string): void {
    if (!this.state.locales.find(candidate => candidate.locale === locale)) {
      throw new Error(`Locale ${locale} is not supported.`);
    }
    const previous = this.state.locale;
    this.patch({ saveFailure: undefined });
    this.applyLocale(locale);
    this.preferenceService.save(SolSettingKey.locale, locale).subscribe({
      error: (error: unknown) => {
        this.applyLocale(previous);
        this.patch({ saveFailure: PreferenceTools.toFailureMessage(error) });
      },
    });
  }

  public setLocales(ids: readonly string[]): void {
    const locales = LocaleService.toOfferedLocales(ids, this.state.locales);
    if (locales.length === 0) {
      return;
    }
    this.patch({ locales });
    if (locales.find(locale => locale.locale === this.state.locale) === undefined) {
      this.applyLocale(locales[0].locale);
    }
  }

  private reconcile(): void {
    this.preferenceService.prePainted().subscribe(values => {
      const locale = this.toAnswered(values[SolSettingKey.locale.name]);
      if (locale === undefined) {
        return;
      }
      // Caching the default too keeps the first frame of the next load of this app from flashing.
      this.preferenceService.cache(SolSettingKey.locale, locale);
      if (locale === this.state.locale) {
        return;
      }
      this.applyLocale(locale);
    });
  }

  /** An absent key expresses no opinion; a `null` one was asked and had no answer, so the default applies. */
  private toAnswered(answer: unknown): string | undefined {
    if (answer === undefined) {
      return undefined;
    }
    return LocaleService.toSupportedLocale(answer === null ? this.state.defaultLocale : answer, this.state.locales);
  }

  private applyLocale(locale: string): void {
    this.patch({
      locale: locale,
    });
    this.refreshBundles().subscribe({
      error: err => {
        console.error('[LocaleStore] Error refreshing translation bundles:', err);
      },
    });
  }

  private refreshBundles(): Observable<void> {
    const state = this.state;
    const languageId =
      state.locales.find(locale => locale.locale === state.locale)?.languageId ?? SolLocales.en.languageId;
    return this.translationBundleService.getBundles(state.bundleNames, languageId).pipe(
      tap(translations => {
        if (this.state.locale !== state.locale) {
          return;
        }
        this.patch({
          translations: translations,
          translationsLocale: state.locale,
        });
      }),
      map(() => void 0),
    );
  }

  /**
   * Translate a SolText, either a translatable key with optional params, or a raw string.
   */
  public translateText(text: SolText | undefined) {
    // Nothing to translate
    if (text === undefined) {
      return '';
    }

    // Translatable key with params
    if (text.translatable) {
      return this.translate(text.text, text.params);
    }

    // Return raw text
    return text.text;
  }

  /**
   * Translate pipe can be used to translate a key with optional parameters. Examples:
   * ```typescript
   * const translated1 = localeStore.translate('hello-key');
   * const translated2 = localeStore.translate('hello-name-key', { name: 'Jane' });
   * ```
   * The json file should contain the keys and corresponding templates:
   * ```json
   * {
   *   "hello-key": "Hello",
   *   "hello-name-key": "Hello {{name}}"
   * }
   * ```
   */
  public translate(key: string | undefined, params: Record<string, any> = {}): string {
    if (key === undefined || key.trim() === '') {
      return '';
    }

    const translations = this.state.translations;
    if (translations[key]) {
      const template = translations[key]!;
      return LocaleService.interpolate(this.state.translations, template, params);
    }
    return key;
  }

  private static toSupportedLocale(value: unknown, locales: SolLocale[]): string | undefined {
    return locales.some(candidate => candidate.locale === value) ? (value as string) : undefined;
  }

  private static toOfferedLocales(ids: readonly string[], locales: SolLocale[]): SolLocale[] {
    return ids.flatMap(id => {
      const registered = locales.find(candidate => candidate.locale === id);
      return registered === undefined ? [] : [registered];
    });
  }

  private static interpolate(
    translations: Record<string, string>,
    template: string,
    params: Record<string, any>,
  ): string {
    return template.replace(/\{\{(.*?)\}\}/g, (_, key) => {
      const value = params[key.trim()];
      const translatedValue = value ? translations[value] : value;
      const finalValue = translatedValue !== undefined ? translatedValue : value;
      return finalValue !== undefined ? finalValue : `{{${key}}}`;
    });
  }
}
